import { Component, Inject, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IChangeSet } from 'src/app/changes-dialog/changes-dialog.component';
import { ExperimentFormatPipe } from 'src/app/pipes/experiment-format.pipe';
import { LabpartnerService } from 'src/app/services/labpartner.service';
import {
  AuditedChangeResponse,
  ExperimentExtended,
  ExperimentType,
  ExperimentTypeDisplayName,
  ICExperiment,
} from 'src/app/services/labpartner.service.model';
import {
  EVENT_CREATE_EXPERIMENT,
  EVENT_MODIFY_EXPERIMENT,
} from 'src/app/services/logging-constants';
import { LoggingService } from 'src/app/services/logging.service';
import { DialogService } from 'src/app/shared/dialog.service';
import { ExperimentService } from 'src/app/shared/experiment.service';
import { NotificationService } from 'src/app/shared/notification.service';
import { BaseComponent } from 'src/app/support/base.component';

export interface ICExperimentDialogData {
  isReadOnly: boolean;
  isEditMode: boolean;
  experiment: ICExperiment & Partial<ExperimentExtended>;
}

export interface ICExperimentDialogResult {
  success: boolean;
  data: ICExperiment;
}

@Component({
  selector: 'ic-experiment-dialog',
  templateUrl: './ic-experiment-dialog.component.html',
  styleUrls: ['./ic-experiment-dialog.component.scss'],
  standalone: false,
})
export class ICExperimentDialogComponent extends BaseComponent implements OnDestroy {
  isLoading: boolean = false;
  wasCanceled: boolean = false;
  hideExperimentType: boolean = false;

  ExperimentTypeIndexes = Object.keys(ExperimentTypeDisplayName);
  ExperimentTypeDisplayNames = ExperimentTypeDisplayName;

  initialFormValues!: ICExperiment;

  experimentForm!: UntypedFormGroup;

  constructor(
    private _fb: UntypedFormBuilder,
    public experimentService: ExperimentService,
    public dialogRef: MatDialogRef<ICExperimentDialogComponent>,
    private apiService: LabpartnerService,
    private notificationService: NotificationService,
    private loggingService: LoggingService,
    private dialogService: DialogService,
    private experimentFormatPipe: ExperimentFormatPipe,
    @Inject(MAT_DIALOG_DATA) public data: ICExperimentDialogData,
  ) {
    super();
    this.initForm();
  }

  initForm(): void {
    this.initialFormValues = { ...this.data.experiment };

    this.experimentForm = this._fb.group({
      name: [this.data.experiment.name, Validators.required],
      description: [this.data.experiment.description],
      type: [this.data.experiment.type],
    });
  }

  protected ngOnDestroyInternal(): void {
    // required by base component. clean up any component specific resources
  }

  onCancel() {
    this.notificationService.warn(this.data.isReadOnly ? ':: Closed' : ':: Canceled');
    const dialogresult = { ok: false, data: null };
    this.onClose(dialogresult);
  }

  async onSubmit() {
    const experiment: ICExperiment = this.experimentForm.getRawValue();
    experiment.experimentId = this.data.experiment.experimentId;

    let dialogResult: ICExperimentDialogResult = { success: false, data: this.data.experiment };

    if (!this.data.isEditMode) {
      this.loadingDisableForm();

      try {
        const createdExperiment: ICExperiment | undefined = await this.apiService
          .createICExperiment(experiment)
          .toPromise();

        if (!createdExperiment) {
          throw new Error('createdExperiment is null');
        }

        this.notificationService.success(':: Experiment created successfully');
        dialogResult = { success: true, data: createdExperiment };

        const props: { [key: string]: number } = {
          ExperimentId: createdExperiment.experimentId,
        };
        this.loggingService.logEvent(EVENT_CREATE_EXPERIMENT, props);
      } catch (err: any) {
        this.notificationService.warn(':: Error creating experiment');
        dialogResult = { success: false, data: err };
      } finally {
        this.onClose(dialogResult);
      }
    } else {
      const fieldsToDiff = ['name', 'description'];

      const changeSet: IChangeSet[] = [];

      for (const key of fieldsToDiff) {
        if ((this.initialFormValues as any)[key] != this.experimentForm.getRawValue()[key]) {
          changeSet.push({
            identifier: this.experimentFormatPipe.transform(
              this.initialFormValues.experimentId,
              this.initialFormValues.type,
            ),
            field: `${
              key[0].toLocaleUpperCase() +
              key
                .slice(1)
                .replace(/([A-Z])/g, ' $1')
                .trim()
            }`,
            oldValue: (this.initialFormValues as any)[key],
            newValue: this.experimentForm.value[key],
            // C# field name
            data: `${key[0].toLocaleUpperCase() + key.slice(1).trim()}`,
          });
        }
      }

      if (changeSet.length == 0) {
        this.notificationService.warn('No changes have been made.');
        return;
      }

      const dialogRef = this.dialogService.openConfirmChangesDialog(
        changeSet,
        this.initialFormValues.type,
        {
          multiReason: true,
        },
      );

      dialogRef.afterClosed().subscribe(async dialogData => {
        this.loadingDisableForm();
        if (
          dialogData?.submitClicked &&
          (dialogData?.reasonProvided ||
            this.initialFormValues.type == ExperimentType.ResearchAndDevelopment)
        ) {
          try {
            const fieldReasonMap: { [key: string]: string } = {};
            for (let i = 0; i < changeSet.length; i++) {
              fieldReasonMap[changeSet[i].data!] = dialogData.fieldReasons[i];
            }
            const result = await this.apiService
              .updateICExperiment(experiment, dialogData.batchReason ?? '', fieldReasonMap)
              .toPromise();

            if (!result) {
              throw new Error('Experiment is null');
            }

            this.notificationService.success(':: Experiment updated successfully');
            dialogResult = { success: true, data: result };

            const props: { [key: string]: number } = {
              ExperimentId: experiment.experimentId,
            };
            this.loggingService.logEvent(EVENT_MODIFY_EXPERIMENT, props);
          } catch (err: any) {
            dialogResult = { success: false, data: err };

            const expectedErr = err.error as AuditedChangeResponse;

            if (expectedErr.expectedException) {
              this.notificationService.error(expectedErr.message);
            } else {
              throw err;
            }
          } finally {
            this.onClose(dialogResult);
          }
        } else {
        }
      });
    }
  }

  onClose(dialogResult: any) {
    this.dialogRef.close(dialogResult);
  }

  private loadingDisableForm(): void {
    this.isLoading = true;
    this.experimentForm.disable();
  }
}
